import React, { useState, useEffect, useContext } from 'react';
import { Card, Button, Table, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import DocumentTypeTag from '../projects/DocumentTypeTag';
import { downloadDocument } from '../../utils/documentUtils';
import { FaDownload, FaTrash } from 'react-icons/fa';

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  
  const { token } = useContext(AuthContext);
  
  // Fetch projects when component mounts
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await fetch('/api/projects', {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch projects');
        }
        
        const data = await response.json();
        setProjects(data);
        
        // Set default selected project if available
        if (data.length > 0) {
          setSelectedProject(data[0].ProjectId);
          fetchDocuments(data[0].ProjectId);
        } else {
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Error fetching projects:', err);
        toast.error('Failed to load projects');
        setIsLoading(false);
      }
    };
    
    fetchProjects();
  }, [token]);
  
  // Fetch documents for a project
  const fetchDocuments = async (projectId) => {
    setIsLoading(true);
    
    try {
      const response = await fetch(`/api/documents/project/${projectId}`, {
        headers: {
          'x-auth-token': token
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch documents');
      }
      
      const data = await response.json();
      setDocuments(data);
    } catch (err) {
      console.error('Error fetching documents:', err);
      toast.error('Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle project selection change
  const handleProjectChange = (e) => {
    const projectId = e.target.value;
    setSelectedProject(projectId);
    fetchDocuments(projectId);
  };
  
  // Handle document deletion
  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }
    
    try {
      const response = await fetch(`/api/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          'x-auth-token': token
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete document');
      }
      
      // Remove the deleted document from state
      setDocuments(documents.filter(doc => doc.DocumentId !== documentId));
      toast.success('Document deleted successfully');
    } catch (err) {
      console.error('Error deleting document:', err);
      toast.error('Failed to delete document');
    }
  };
  
  if (projects.length === 0) {
    return (
      <div>
        <h2 className="mb-4">Documents</h2>
        <Card>
          <Card.Body className="text-center">
            <p>No projects found. Create a project first to manage documents.</p>
            <Button as={Link} to="/projects/create" variant="primary">
              Create Project
            </Button>
          </Card.Body>
        </Card>
      </div>
    );
  }
  
  return (
    <div>
      <h2 className="mb-4">Documents</h2>
      
      <Card className="mb-4">
        <Card.Body>
          <Form.Group>
            <Form.Label>Select Project</Form.Label>
            <Form.Select
              value={selectedProject}
              onChange={handleProjectChange}
            >
              {projects.map(project => (
                <option key={project.ProjectId} value={project.ProjectId}>
                  {project.ProjectName}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Card.Body>
      </Card>
      
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Project Documents</h5>
          <Button
            as={Link}
            to={`/projects/${selectedProject}`}
            variant="primary"
            size="sm"
          >
            Manage Project
          </Button>
        </Card.Header>
        <Card.Body>
          {isLoading ? (
            <div className="text-center py-4">Loading documents...</div>
          ) : (
            <>
              {documents.length > 0 ? (
                <div className="table-responsive">
                  <Table striped hover>
                    <thead>
                      <tr>
                        <th>Document Name</th>
                        <th>Type</th>
                        <th>Date Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documents.map(doc => (
                        <tr key={doc.DocumentId}>
                          <td>{doc.DocumentName}</td>
                          <td><DocumentTypeTag type={doc.DocumentType} /></td>
                          <td>{new Date(doc.CreatedAt).toLocaleDateString()}</td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="me-2"
                              onClick={() => {
                                try {
                                  downloadDocument(doc.DocumentId, doc.DocumentName, token);
                                } catch (err) {
                                  toast.error('Error downloading document');
                                }
                              }}
                            >
                              <FaDownload className="me-1" /> Download
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteDocument(doc.DocumentId)}
                            >
                              <FaTrash className="me-1" /> Delete
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <p className="text-center">
                  No documents found for this project. Go to the project page to upload or generate documents.
                </p>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default Documents;
